import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from 'stories/components';
import { Card, CardBody, CardHeader, Container, Row, Col } from 'stories/layout';
import { useUserGuides } from 'components/UserGuide';
import './FloatingHelpButton.css';

// Simple Guide Viewer Component
const SimpleGuideViewer = ({ guide, onClose }) => {
  const { t } = useTranslation();
  
  if (!guide) return null;

  return (
    <div className="floating-guide-overlay">
      <div className="floating-guide-container">
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <Typography variant="h3" className="mb-0">
              {guide.title}
            </Typography>
            <Button variant="link" onClick={onClose} className="text-muted">
              <i className="fas fa-times fa-lg" />
            </Button>
          </CardHeader>
          <CardBody>
            <Container fluid>
              <Typography variant="medium" className="mb-4 text-muted">
                {guide.description}
              </Typography>

              {/* All steps as simple list */}
              {guide.steps && guide.steps.map((step, index) => (
                <div key={index} className="mb-4">
                  <Typography variant="h5" className="mb-2">
                    <i className="fas fa-arrow-right text-primary mr-2" />
                    {step.title}
                  </Typography>
                  
                  <Typography variant="medium" className="mb-3">
                    {step.description}
                  </Typography>

                  {/* Actions */}
                  {step.actions && (
                    <div className="mb-3">
                      {step.actions.map((action, actionIndex) => (
                        <div key={actionIndex} className="mb-1">
                          <i className="fas fa-check text-success mr-2" />
                          <Typography variant="small" className="d-inline">
                            {action}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* UI Elements */}
                  {step.ui_elements && (
                    <div className="mb-3">
                      {step.ui_elements.map((element, elemIndex) => (
                        <div key={elemIndex} className="mb-1">
                          <i className="fas fa-mouse-pointer text-info mr-2" />
                          <Typography variant="small" className="d-inline">
                            <strong>{element.label}</strong>
                            {element.required && <span className="text-danger ml-1">*</span>}
                            {element.description && ` - ${element.description}`}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tips */}
                  {step.tips && (
                    <div className="mb-3">
                      {step.tips.map((tip, tipIndex) => (
                        <div key={tipIndex} className="mb-1">
                          <i className="fas fa-lightbulb text-warning mr-2" />
                          <Typography variant="small" className="d-inline">
                            {tip}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Calculation helpers */}
                  {step.calculation_helpers && (
                    <div className="mb-3">
                      {step.calculation_helpers.map((helper, helperIndex) => (
                        <div key={helperIndex} className="mb-1">
                          <i className="fas fa-calculator text-secondary mr-2" />
                          <Typography variant="small" className="d-inline">
                            <strong>{helper.button}:</strong> {helper.function}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Common Errors */}
              {guide.common_errors && (
                <div className="mt-4">
                  <Typography variant="h5" className="mb-3">
                    <i className="fas fa-exclamation-triangle text-warning mr-2" />
                    {t('common_errors', 'Yleisiä virheitä')}
                  </Typography>
                  {guide.common_errors.map((error, errorIndex) => (
                    <div key={errorIndex} className="mb-2">
                      <div className="mb-1">
                        <i className="fas fa-times text-danger mr-2" />
                        <Typography variant="small" className="d-inline font-weight-bold">
                          {error.error}
                        </Typography>
                      </div>
                      <div className="ml-4">
                        <Typography variant="small" className="text-muted">
                          {t('solution', 'Ratkaisu')}: {error.solution}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              {guide.summary && (
                <div className="mt-4">
                  <Typography variant="h5" className="mb-3">
                    <i className="fas fa-check-circle text-success mr-2" />
                    {t('summary', 'Yhteenveto')}
                  </Typography>
                  <Typography variant="medium" className="mb-3">
                    {guide.summary.description}
                  </Typography>
                  
                  {guide.summary.next_steps && (
                    <div>
                      <Typography variant="h6" className="mb-2">
                        {t('next_steps', 'Seuraavat vaiheet')}:
                      </Typography>
                      {guide.summary.next_steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="mb-1">
                          <i className="fas fa-arrow-right text-primary mr-2" />
                          <Typography variant="small" className="d-inline">
                            {step}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="text-center mt-4">
                <Button variant="primary" onClick={onClose}>
                  {t('close', 'Sulje')}
                </Button>
              </div>
            </Container>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

const FloatingHelpButton = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const { getGuideById } = useUserGuides();

  // Quick access guides - most commonly used
  const quickGuides = [
    {
      id: 'purchase_transaction',
      title: t('how_to_add_purchase', 'Miten lisään osto salkkuun?')
    },
    {
      id: 'selling_transaction', 
      title: t('how_to_add_sale', 'Miten lisään myynti salkkuun?')
    },
    {
      id: 'dividend_transaction',
      title: t('how_to_add_dividend', 'Miten lisään osingon?')
    },
    {
      id: 'portfolio_management',
      title: t('how_to_manage_portfolio', 'Miten hallitsen salkkuja?')
    }
  ];

  const handleQuickGuideClick = (guideId) => {
    const guide = getGuideById(guideId);
    setSelectedGuide(guide);
    setIsOpen(false);
  };

  const handleCloseGuide = () => {
    setSelectedGuide(null);
  };

  const toggleHelp = () => {
    setIsOpen(!isOpen);
  };

  if (selectedGuide) {
    return (
      <SimpleGuideViewer 
        guide={selectedGuide} 
        onClose={handleCloseGuide}
      />
    );
  }

  return (
    <>
      {/* Floating Help Button */}
      <div className={`floating-help-button ${isOpen ? 'open' : ''}`}>
        <Button
          variant="primary"
          className="help-button-main"
          onClick={toggleHelp}
          title={t('help_and_guides', 'Ohje ja ohjeet')}
        >
          <i className={`fas ${isOpen ? 'fa-times' : 'fa-question-circle'} fa-lg`} />
        </Button>

        {/* Quick Help Menu */}
        {isOpen && (
          <div className="help-menu">
            <Card className="help-menu-card">
              <CardHeader className="bg-primary text-white py-2">
                <Typography variant="h6" className="text-white mb-0">
                  <i className="fas fa-graduation-cap mr-2" />
                  {t('quick_help', 'Pikaohjeet')}
                </Typography>
              </CardHeader>
              <CardBody className="p-2">
                {/* Quick Guide Buttons */}
                <div className="quick-guides">
                  {quickGuides.map(guide => (
                    <Button
                      key={guide.id}
                      size="sm"
                      variant="default"
                      className="quick-guide-button w-100 mb-2"
                      onClick={() => handleQuickGuideClick(guide.id)}
                    >
                      <Typography variant="h3" className="text-dark text-center font-weight-normal">
                        {guide.title}
                      </Typography>
                    </Button>
                  ))}
                </div>

                {/* Separator */}
                <hr className="my-2" />


                {/* Contact Support */}
                <Button
                  size="sm"
                  variant="default"
                  className="w-100"
                  onClick={() => window.open('mailto:support@investime.fi', '_blank')}
                >
                  <Typography variant="h3" className="text-dark text-center font-weight-normal">
                    {t('contact_support', 'Ota yhteyttä tukeen')}
                  </Typography>
                </Button>
              </CardBody>
            </Card>
          </div>
        )}
      </div>

      {/* Overlay to close menu when clicking outside */}
      {isOpen && (
        <div 
          className="help-menu-overlay" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default FloatingHelpButton;
